/*
 * Copyright © 2018-2025, GlobalVET AB
 *
 * All rights reserved. No part or the whole of this source code and the compiled program
 * may be reproduced, copied, distributed, disseminated to the public, adapted or transmitted
 * in any form or by any means, including photocopying, recording, or other electronic or
 * mechanical methods, without the prior written permission of GlobalVET AB. This source code
 * and the compiled program may only be used for the purposes of GlobalVET AB. This source code
 * and the compiled program shall be kept confidential and shall not be made public or made
 * available or disclosed to any unauthorized person. Any dispute or claim arising out of the
 * breach of these provisions shall be governed by and construed in accordance with the
 * laws of Sweden.
 */

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { strings } from "../../../common/Strings/Strings";
import Button from "../../../components/Button";
import AlertBox from "../../../components/AlertBox";
import ClinicEmploymentApi from "../../../api/ClinicEmploymentApi";
import { useUser } from "../../../contexts/UserContext";
import { ClinicEmploymentType } from "../../../models/employment/ClinicEmploymentType";
import { getGeneralError } from "../../../util/helperFunctions";

interface Props {
  clinicId?: string;
  onFinish(): void;
}

type Form = { type: ClinicEmploymentType };

const SuccessTab: React.FC<Props> = ({ clinicId, onFinish }: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { handleSubmit, register } = useForm<Form>({ defaultValues: { type: ClinicEmploymentType.VET } });
  const { user } = useUser();

  const submitSelfEmploymentRole = async ({ type }: Form) => {
    if (type === ClinicEmploymentType.MANAGER) {
      onFinish();
    } else if (user.confirmedEmail) {
      setLoading(true);
      try {
        await ClinicEmploymentApi.addClinicEmployee(clinicId, { email: user.confirmedEmail.value, type });
        onFinish();
      } catch (err) {
        setError(await getGeneralError(err));
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="flex flex-col items-center justify-center space-y-6">
      <form
        onSubmit={handleSubmit(submitSelfEmploymentRole)}
        className="md:tw-card w-full space-y-6 md:max-w-md md:p-8"
      >
        <div>
          <h1 className="text-xl font-semibold leading-tight text-zinc-800 dark:text-white lg:text-2xl">
            {strings.chooseEmploymentInClinicTitle}
          </h1>
        </div>
        <div>
          <p>{strings.chooseEmploymentInClinicInfo}</p>
        </div>
        <div className="tw-card divide-y divide-gray-200 dark:divide-gray-700">
          <div className="p-4">
            <div className="flex">
              <input className="cursor-pointer" {...register("type")} type="radio" value={ClinicEmploymentType.VET} />
              <label>{strings.chooseEmploymentInClinicOption1}</label>
            </div>
          </div>
          <div className="p-4">
            <div className="flex">
              <input className="cursor-pointer" {...register("type")} type="radio" value={ClinicEmploymentType.ASSISTANT} />
              <label>{strings.chooseEmploymentInClinicOption2}</label>
            </div>
          </div>
          <div className="p-4">
            <div className="flex">
              <input className="cursor-pointer" {...register("type")} type="radio" value={ClinicEmploymentType.MANAGER} />
              <label>{strings.chooseEmploymentInClinicOption3}</label>
            </div>
          </div>
        </div>
        <AlertBox message={error} />
        <div>
          <Button disabled={loading} type="submit" loading={loading}>
            {strings.save}
          </Button>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="flex justify-start">
            <span className="text-left text-sm font-medium text-gray-500 dark:text-gray-400">
              {strings.chooseLater}
            </span>
          </div>
          <div className="flex justify-end">
            <Button
              className="text-right text-sm"
              onClick={() => {
                onFinish();
              }}
              variant="link"
            >
              {strings.skipForNow}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SuccessTab;
